(() => {
  const instances = new Map();
  const failedHosts = new WeakSet();
  let nextId = 0;

  function findSelect(root) {
    if (!root) return null;
    const select = root.querySelector('select');
    if (select) return select;
    for (const element of root.querySelectorAll('*')) {
      if (element.shadowRoot) {
        const nested = findSelect(element.shadowRoot);
        if (nested) return nested;
      }
    }
    return null;
  }

  function createPicker(host) {
    const id = `og-frequency-${++nextId}`;
    const wrapper = document.createElement('div');
    wrapper.className = 'og-frequency-picker relative';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'select-with-image default-button';
    button.setAttribute('aria-controls', id);
    button.setAttribute('aria-expanded', 'false');
    const valueLabel = document.createElement('span');
    button.append(valueLabel);
    button.insertAdjacentHTML('beforeend', '<svg role="presentation" focusable="false" width="10" height="7" class="select-chevron" viewBox="0 0 10 7"><path d="m1 1 4 4 4-4" fill="none" stroke="currentColor" stroke-width="2"></path></svg>');
    const popover = document.createElement('x-popover');
    if (typeof popover.show !== 'function' || typeof popover.hide !== 'function' || !popover.shadowRoot?.querySelector('[part="content"]')) {
      throw new Error('Theme popover is not ready');
    }
    popover.id = id;
    popover.className = 'popover og-frequency-popover';
    popover.setAttribute('initial-focus', 'input:checked:not(:disabled)');
    popover.setAttribute('close-on-listbox-select', '');
    popover.setAttribute('anchor-horizontal', 'start');
    popover.setAttribute('anchor-vertical', 'end');
    const title = document.createElement('p');
    title.className = 'h5';
    title.slot = 'title';
    const list = document.createElement('div');
    list.className = 'popover-listbox';
    popover.append(title, list);
    wrapper.append(button, popover);

    let nativeSelect;
    let hiddenElement;
    let originalDisplay;
    let originalTabindex;
    let originalAriaHidden;
    let signature;
    let disposed = false;
    let openCheck;

    function destroy() {
      disposed = true;
      clearTimeout(openCheck);
      releaseSelect();
      wrapper.remove();
      try {
        if (popover.open) {
          popover.addEventListener('dialog:after-hide', () => popover.remove(), { once: true });
          Promise.resolve(popover.hide()).catch(() => popover.remove());
          setTimeout(() => popover.remove(), 1000);
        } else popover.remove();
      } catch (_) {
        popover.remove();
      }
    }

    function fallback(error) {
      failedHosts.add(host);
      instances.delete(host);
      destroy();
      console.warn('[OG frequency picker] Restored native selector.', error);
    }

    function safely(callback) {
      if (disposed) return;
      try {
        return callback();
      } catch (error) {
        fallback(error);
      }
    }

    function sync() {
      safely(update);
    }

    function releaseSelect() {
      if (!nativeSelect) return;
      hiddenElement.style.removeProperty('display');
      if (originalDisplay.value) hiddenElement.style.setProperty('display', originalDisplay.value, originalDisplay.priority);
      for (const [name, value] of [['tabindex', originalTabindex], ['aria-hidden', originalAriaHidden]]) {
        if (value === null) nativeSelect.removeAttribute(name);
        else nativeSelect.setAttribute(name, value);
      }
      nativeSelect.removeEventListener('change', sync);
      nativeSelect = null;
    }

    function update() {
      if (disposed) return;
      const select = findSelect(host.shadowRoot) || findSelect(host);
      if (!select || !select.options.length) {
        wrapper.hidden = true;
        if (popover.open) popover.hide();
        releaseSelect();
        return;
      }
      if (select !== nativeSelect) {
        releaseSelect();
        nativeSelect = select;
        const selectHost = select.getRootNode().host;
        hiddenElement = selectHost?.matches('og-select') ? selectHost : select;
        originalDisplay = { value: hiddenElement.style.getPropertyValue('display'), priority: hiddenElement.style.getPropertyPriority('display') };
        originalTabindex = select.getAttribute('tabindex');
        originalAriaHidden = select.getAttribute('aria-hidden');
        select.addEventListener('change', sync);
      }
      const options = Array.from(select.options, option => ({
        value: option.value,
        text: option.textContent.trim(),
        disabled: option.disabled || option.parentElement?.disabled === true
      }));
      const newSignature = JSON.stringify(options);
      if (signature !== newSignature) {
        if (popover.open) popover.hide();
        signature = newSignature;
        list.replaceChildren(...options.map((option, index) => {
          const label = document.createElement('label');
          label.className = `popover-listbox__option${option.disabled ? ' is-disabled' : ''}`;
          const radio = document.createElement('input');
          radio.type = 'radio';
          radio.className = 'sr-only';
          radio.id = `${id}-${index}`;
          radio.name = id;
          radio.value = option.value;
          radio.disabled = option.disabled;
          label.htmlFor = radio.id;
          label.append(radio, document.createTextNode(option.text));
          return label;
        }));
      }
      const titleText = select.getAttribute('aria-label') || host.parentElement.querySelector('og-text[key="offerEveryLabel"]')?.textContent.trim() || '';
      if (title.textContent !== titleText) title.textContent = titleText;
      const selectedText = select.selectedOptions[0]?.textContent.trim() || '';
      if (valueLabel.textContent !== selectedText) valueLabel.textContent = selectedText;
      button.setAttribute('aria-label', `${titleText}: ${valueLabel.textContent}`);
      button.disabled = select.disabled;
      for (const radio of list.querySelectorAll('input')) radio.checked = radio.value === select.value;
      if (!wrapper.isConnected) host.after(wrapper);
      wrapper.hidden = host.hidden || getComputedStyle(host).display === 'none';
      if ((wrapper.hidden || button.disabled) && popover.open) popover.hide();
      if (!wrapper.isConnected || !popover.isConnected || !list.querySelector('input')) {
        throw new Error('Custom selector is incomplete');
      }
      if (!['absolute', 'fixed'].includes(getComputedStyle(popover).position)) {
        releaseSelect();
        wrapper.hidden = true;
        if (popover.open) Promise.resolve(popover.hide()).catch(fallback);
        return;
      }
      hiddenElement.style.setProperty('display', 'none', 'important');
      select.setAttribute('tabindex', '-1');
      select.setAttribute('aria-hidden', 'true');
    }

    popover.addEventListener('change', event => safely(() => {
      const radio = event.target;
      if (!radio.matches('input[type="radio"]') || radio.disabled) return;
      const select = findSelect(host.shadowRoot) || findSelect(host);
      if (!select || select.disabled) return;
      if (select.value !== radio.value) {
        select.value = radio.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      sync();
      queueMicrotask(sync);
    }));
    button.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      safely(() => {
        update();
        if (button.disabled || wrapper.hidden) return;
        clearTimeout(openCheck);
        if (popover.open) {
          Promise.resolve(popover.hide()).catch(fallback);
        } else {
          Promise.resolve(popover.show()).catch(fallback);
          if (!popover.open) throw new Error('Custom selector failed to open');
          openCheck = setTimeout(() => safely(() => {
            if (popover.open && (!popover.getClientRects().length || getComputedStyle(popover).visibility === 'hidden')) {
              throw new Error('Custom selector failed to open');
            }
          }), 1500);
        }
      });
    });

    return { sync, destroy };
  }

  const scopeSelector = '.shopify-section--main-product, quick-buy-drawer';
  const pollingWindows = new Map();

  function refresh(scope) {
    for (const [host, picker] of instances) {
      if (!host.isConnected) {
        picker.destroy();
        instances.delete(host);
      }
    }
    for (const host of scope.querySelectorAll('og-select-frequency')) {
      if (failedHosts.has(host)) continue;
      try {
        if (!instances.has(host)) {
          const select = findSelect(host.shadowRoot) || findSelect(host);
          if (!select?.options.length) continue;
          instances.set(host, createPicker(host));
        }
        instances.get(host).sync();
      } catch (error) {
        failedHosts.add(host);
        instances.get(host)?.destroy();
        instances.delete(host);
        console.warn('[OG frequency picker] Keeping native selector.', error);
      }
    }
  }

  function stopPolling(scope) {
    clearTimeout(pollingWindows.get(scope)?.timer);
    pollingWindows.delete(scope);
  }

  function startPolling(scope) {
    if (!scope?.isConnected || pollingWindows.has(scope)) return;
    const state = { deadline: performance.now() + 5000, timer: null };
    pollingWindows.set(scope, state);
    function tick() {
      if (!scope.isConnected || performance.now() >= state.deadline) {
        stopPolling(scope);
        return;
      }
      refresh(scope);
      state.timer = setTimeout(tick, 500);
    }
    tick();
  }

  async function init() {
    await customElements.whenDefined('x-popover');
    document.querySelectorAll('.shopify-section--main-product, quick-buy-drawer[open]').forEach(startPolling);
    const onProductUpdate = event => {
      const scope = event.target.closest?.(scopeSelector);
      if (scope) queueMicrotask(() => startPolling(scope));
    };
    document.addEventListener('variant:change', onProductUpdate);
    document.addEventListener('product:rerender', onProductUpdate, true);
    document.addEventListener('toggle', event => {
      if (!event.target.matches?.('quick-buy-drawer')) return;
      if (event.target.open) startPolling(event.target);
      else stopPolling(event.target);
    });
    document.addEventListener('shopify:section:load', event => {
      if (event.target.matches(scopeSelector)) startPolling(event.target);
      event.target.querySelectorAll('.shopify-section--main-product, quick-buy-drawer[open]').forEach(startPolling);
    });
    document.addEventListener('shopify:section:unload', event => {
      for (const scope of pollingWindows.keys()) {
        if (scope === event.target || event.target.contains(scope)) stopPolling(scope);
      }
      for (const [host, picker] of instances) {
        if (event.target.contains(host)) {
          picker.destroy();
          instances.delete(host);
        }
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
